/**
 * DiscardTrainer - Statistically Optimal Discard Trainer & Hand Analyzer
 * Computes all 15 combinations of keeping 4 cards and discarding 2 cards from a 6-card hand.
 * Evaluates exact Hand Expected Value (EV) across all 46 unseen cut cards,
 * calculates floor/ceiling hand ranges, estimates crib impact, and ranks combinations.
 */

import { Card } from '../models/Card.js';
import { Deck } from '../models/Deck.js';
import { CribbageScorer, combinations } from '../scoring/CribbageScorer.js';
import { CribbageAI } from '../ai/CribbageAI.js';
import { VAL } from '../scoring/ScoringConstants.js';
import { AchievementManager } from '../achievements/AchievementManager.js';
import { Profile } from '../profile/Profile.js';

// Established baseline expected crib values for 2-card discard pairs (Dealer vs Pone)
const BASE_CRIB_VALUES = {
  '5-5': 8.8,
  '2-3': 5.4,
  '7-8': 5.3,
  '4-5': 5.6,
  '5-6': 5.7,
  '3-3': 5.0,
  '4-4': 4.7,
  '6-6': 4.9,
  '7-7': 5.2,
  '8-8': 5.0,
  '9-9': 4.6,
  '10-10': 4.4,
  'J-J': 4.8,
  'Q-Q': 4.4,
  'K-K': 4.2,
  'A-A': 4.3,
  'A-4': 4.9,
  '2-4': 4.5,
  '6-9': 4.7,
  '7-9': 4.6,
  '8-9': 4.8,
  'J-5': 6.2,
  'Q-5': 5.9,
  'K-5': 5.8,
  '10-5': 5.9,
  'A-5': 5.6,
  '2-5': 5.5,
  '3-5': 5.7
};

export class DiscardTrainer {
  /**
   * Session state for continuous practice statistics.
   */
  static stats = {
    handsPlayed: 0,
    optimalCount: 0,
    totalLossEV: 0
  };

  /**
   * Resets session statistics, and optionally career statistics in profile.
   * @param {Object} [options={}]
   */
  static resetStats(options = {}) {
    DiscardTrainer.stats = {
      handsPlayed: 0,
      optimalCount: 0,
      totalLossEV: 0
    };
    if (options && options.career) {
      try {
        Profile.getInstance(options.storage).clearDiscardTrainerStats(options.storage);
      } catch (_) {}
    }
  }

  /**
   * Retrieves current session stats and global career stats from Profile.
   * @param {Object} [storage=null]
   * @returns {{ session: Object, career: Object }}
   */
  static getStats(storage = null) {
    const session = {
      ...DiscardTrainer.stats,
      accuracyPct: DiscardTrainer.stats.handsPlayed > 0
        ? Math.round((DiscardTrainer.stats.optimalCount / DiscardTrainer.stats.handsPlayed) * 100)
        : 0
    };
    let career = null;
    try {
      career = Profile.getInstance(storage).getDiscardTrainerStats(storage);
    } catch (_) {}
    return { session, career };
  }

  /**
   * Retrieves player's global career stats across all brands.
   * @param {Object} [storage=null]
   * @returns {Object}
   */
  static getCareerStats(storage = null) {
    try {
      return Profile.getInstance(storage).getDiscardTrainerStats(storage);
    } catch (_) {
      return null;
    }
  }

  /**
   * Deals 6 random cards from a fresh 52-card deck.
   * @returns {Card[]}
   */
  static dealRandomHand() {
    const deck = Deck.makeDeck(1, false);
    const shuffled = Deck.shuffleArray(deck);
    return shuffled.slice(0, 6);
  }

  /**
   * Parses string representation or validates array into 6 Card instances.
   * e.g. "5H, 5D, JC, QS, 7S, 8C" or ["5H", "5D", ...]
   * @param {string|Array} input
   * @returns {Card[]|null}
   */
  static parseHand(input) {
    if (Array.isArray(input)) {
      if (input.length === 6 && input.every(c => c instanceof Card || (c && (c.rank !== undefined || c.r !== undefined) && (c.suit || c.s)))) {
        return input.map(c => (c instanceof Card ? c : Card.from(c)));
      }
      return DiscardTrainer.parseHand(input.join(' '));
    }
    if (typeof input !== 'string') return null;

    const tokens = input.trim().toUpperCase().split(/[\s,;]+/);
    if (tokens.length !== 6) return null;

    const rankMap = { A: 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10, J: 11, Q: 12, K: 13 };
    const cards = [];
    const seen = new Set();
    for (const t of tokens) {
      const match = t.match(/^([2-9]|10|[JQKA])([SHDC])$/);
      if (!match) return null;
      const rankToken = match[1];
      const suit = match[2];
      const r = rankMap[rankToken];
      const id = `${rankToken}${suit}`;
      if (seen.has(id)) return null; // duplicate card
      seen.add(id);
      cards.push(new Card({ r, s: suit, id }));
    }
    return cards.length === 6 ? cards : null;
  }

  /**
   * Estimates Expected Crib Value of a 2-card discard pair.
   * @param {Card[]} disc The 2 cards tossed to the crib
   * @param {boolean} isDealer Whether it is the player's own crib
   * @returns {number} Expected points
   */
  static estimateCribEV(disc, isDealer = true) {
    if (!disc || !disc.length) return 4.5;
    if (disc.length === 3) {
      const pairs = combinations(disc, 2);
      let avg = 0;
      for (const p of pairs) {
        avg += DiscardTrainer.estimateCribEV(p, isDealer);
      }
      return Number((avg / pairs.length * 1.5).toFixed(2));
    }
    if (disc.length !== 2) return 4.5;
    const r1 = disc[0].rankName || String(disc[0].rank);
    const r2 = disc[1].rankName || String(disc[1].rank);
    const key1 = `${r1}-${r2}`;
    const key2 = `${r2}-${r1}`;
    let val = BASE_CRIB_VALUES[key1] || BASE_CRIB_VALUES[key2];

    if (val === undefined) {
      const v1 = VAL(disc[0].rank);
      const v2 = VAL(disc[1].rank);
      const sum = v1 + v2;
      val = 3.6; // average cold baseline for crib
      if (sum === 15) val += 2.0;
      if (r1 === r2) val += 2.0;
      if (r1 === '5' || r2 === '5') val += 1.6;
      if (Math.abs(v1 - v2) === 1) val += 0.8; // consecutive connectors
      if (Math.abs(v1 - v2) === 2) val += 0.4; // 1-gap connectors
      if (disc[0].suit === disc[1].suit) val += 0.25; // suited
    }

    return Number(val.toFixed(2));
  }

  /**
   * Evaluates all 15 combinations of keeping 4 cards and discarding 2 cards.
   * @param {Card[]} hand 6 cards
   * @param {boolean} [isDealer=true]
   * @param {Card|null} [starter=null]
   * @returns {{ hand: Card[], isDealer: boolean, combos: Array, optimal: Object }}
   */
  static analyzeHand(hand, isDealer = true, starter = null) {
    if (!hand || (hand.length !== 6 && hand.length !== 9)) {
      throw new Error('DiscardTrainer requires 6 or 9 cards.');
    }

    const discCount = hand.length === 9 ? 3 : 2;
    const fullDeck = Deck.makeDeck(1, false);
    const deadSet = new Set(hand.map(c => c.id));
    const pool = fullDeck.filter(c => !deadSet.has(c.id)); // unseen cut cards
    const combos = combinations(hand, discCount);
    const results = [];

    for (let i = 0; i < combos.length; i++) {
      const disc = combos[i];
      const discIds = new Set(disc.map(c => c.id));
      const keep = hand.filter(c => !discIds.has(c.id));

      let handEV = 0;
      let handMin = Infinity;
      let handMax = -Infinity;

      if (starter) {
        const scored = CribbageScorer.scoreHand(keep, starter, false, { verbose: false });
        handEV = scored ? scored.total : 0;
        handMin = handEV;
        handMax = handEV;
      } else {
        let sum = 0;
        for (const cutCard of pool) {
          const scoreObj = CribbageScorer.scoreHand(keep, cutCard, false, { verbose: false });
          const total = (scoreObj && typeof scoreObj.total === 'number') ? scoreObj.total : 0;
          sum += total;
          if (total < handMin) handMin = total;
          if (total > handMax) handMax = total;
        }
        handEV = sum / pool.length;
      }

      if (handMin === Infinity) handMin = 0;
      if (handMax === -Infinity) handMax = 0;

      const cribEV = DiscardTrainer.estimateCribEV(disc, isDealer);
      const netEV = isDealer ? (handEV + cribEV) : (handEV - cribEV);

      results.push({
        id: disc.map(c => c.id).sort().join('-'),
        disc,
        keep,
        handEV: Number(handEV.toFixed(2)),
        handMin,
        handMax,
        cribEV: Number(cribEV.toFixed(2)),
        netEV: Number(netEV.toFixed(2))
      });
    }

    // Sort descending by Net EV (best choice is rank 1)
    results.sort((a, b) => b.netEV - a.netEV);
    results.forEach((item, index) => {
      item.rank = index + 1;
      item.evDiff = Number((results[0].netEV - item.netEV).toFixed(2));
    });

    return {
      hand,
      isDealer,
      starter,
      combos: results,
      optimal: results[0]
    };
  }

  /**
   * Compares the user's selected 2 discard cards against the optimal ranking.
   * @param {Object} analysisResult Result from analyzeHand()
   * @param {Card[]} userDiscards 2 chosen cards
   * @returns {Object} Evaluation details and strategic advice
   */
  static evaluateUserChoice(analysisResult, userDiscards, recordStats = true) {
    if (!analysisResult || !analysisResult.combos || !userDiscards || (userDiscards.length !== 2 && userDiscards.length !== 3)) {
      return null;
    }

    const userKey = userDiscards.map(c => c.id).sort().join('-');
    const match = analysisResult.combos.find(c => c.id === userKey);
    if (!match) return null;

    const diff = match.evDiff;
    const isOptimal = match.rank === 1;

    let grade = 'optimal';
    let gradeLabel = 'Optimal Choice! 🏆';
    if (diff === 0) {
      grade = 'optimal';
      gradeLabel = 'Optimal Discard! 🏆';
    } else if (diff <= 0.5) {
      grade = 'great';
      gradeLabel = `Great Pick! 👍 (-${diff.toFixed(2)} EV)`;
    } else if (diff <= 1.25) {
      grade = 'good';
      gradeLabel = `Good Play (-${diff.toFixed(2)} EV)`;
    } else {
      grade = 'suboptimal';
      gradeLabel = `Sub-Optimal ⚠️ (-${diff.toFixed(2)} EV)`;
    }

    let careerStats = null;
    if (recordStats) {
      // Record statistics
      DiscardTrainer.stats.handsPlayed++;
      if (isOptimal) {
        DiscardTrainer.stats.optimalCount++;
        const sfx = (typeof window !== 'undefined' ? window.sfx : null);
        AchievementManager.incrementStat('trainerOptimalDiscards', 1, { sfx });
        AchievementManager.unlock('ev_master', { sfx });
      }
      DiscardTrainer.stats.totalLossEV += diff;

      // Record in global Profile across all brands
      try {
        const prof = Profile.getInstance();
        careerStats = prof.recordDiscardTrainerPlay({ isOptimal, diff });
      } catch (err) {
        console.warn('[DiscardTrainer] Failed to record stats in Profile:', err);
      }
    }

    // Strategic feedback generation
    const tips = DiscardTrainer.generateStrategyTips(analysisResult, match);

    return {
      chosen: match,
      optimal: analysisResult.optimal,
      rank: match.rank,
      evDiff: diff,
      diff: diff,
      grade,
      gradeLabel,
      tips,
      stats: {
        ...DiscardTrainer.stats,
        accuracyPct: Math.round((DiscardTrainer.stats.optimalCount / DiscardTrainer.stats.handsPlayed) * 100),
        career: careerStats || (function() {
          try {
            return Profile.getInstance().getDiscardTrainerStats();
          } catch (_) {
            return null;
          }
        })()
      }
    };
  }

  /**
   * Generates actionable tactical cribbage advice for the specific choice.
   * @param {Object} analysis
   * @param {Object} chosen
   * @returns {string[]}
   */
  static generateStrategyTips(analysis, chosen) {
    const tips = [];
    const isDealer = analysis.isDealer;
    const disc = chosen.disc;
    const optDisc = analysis.optimal.disc;

    const hasFive = disc.some(c => c.rank === '5');
    const optHasFive = optDisc.some(c => c.rank === '5');

    if (!isDealer && hasFive && !optHasFive) {
      tips.push("⚠️ Giving 5s to your opponent's crib is high risk. Opponents gain an average of 5.8+ points from 5s.");
    } else if (isDealer && !hasFive && optHasFive) {
      tips.push("💡 Fives in your own crib significantly boost potential 15s and pairs with dealer discard synergy.");
    }

    if (disc[0].rank === disc[1].rank) {
      if (isDealer) {
        tips.push("✨ Seeding a pair into your own crib guarantees at least 2 points plus strong run and 15 synergies.");
      } else {
        tips.push("⚠️ Throwing a pair to your opponent's crib gifts them 2 guaranteed points.");
      }
    }

    if (Math.abs(VAL(disc[0].rank) + VAL(disc[1].rank)) === 15) {
      if (isDealer) {
        tips.push("🎯 Discarding a fifteen to your own crib locks in 2 guaranteed points.");
      } else {
        tips.push("🚫 Never toss a 15 into your opponent's crib unless forced!");
      }
    }

    if (chosen.rank === 1) {
      tips.push("🌟 You maximized the mathematical combination of hand points and crib impact!");
    } else {
      tips.push(`📌 The optimal discard was ${optDisc.map(c => `${c.rank}${c.suit}`).join(' ')}, offering ${analysis.optimal.netEV.toFixed(2)} Net EV vs your ${chosen.netEV.toFixed(2)} Net EV.`);
    }

    return tips;
  }
}
